import { canvasesApi } from '@/api/endpoints/canvases';
import { isApiError } from '@/api/errors';
import type {
  CanvasConnection,
  CanvasDocument,
  CanvasNode,
  CanvasPresence,
  SaveCanvasDocumentPayload,
  SaveCanvasDocumentResponse,
} from '@/api/types';
import { appConfig } from '@/config/app';
import { local } from '@/lib/storage';

/**
 * Синхронизация документа полотна за одним интерфейсом (Q2, NFR-8): сейчас —
 * HTTP-автосохранение документа целиком; реалтайм (Laravel Reverb или Yjs)
 * подключается отдельным адаптером с теми же методами.
 */
export interface CanvasSync {
  load(canvasId: number): Promise<CanvasDocument>;
  save(canvasId: number, payload: SaveCanvasDocumentPayload): Promise<SaveCanvasDocumentResponse>;
  /** Подписка на присутствие участников; возвращает отписку. Пока только демо-курсоры в mock. */
  subscribePresence(canvasId: number, onChange: (cursors: CanvasPresence[]) => void): () => void;
}

const DEMO_PRESENCE: CanvasPresence[] = [
  { user_id: 2, name: 'Maria R.', initials: 'MR', role: 'editor', x: 640, y: 380 },
  { user_id: 3, name: 'Dan L.', initials: 'DL', role: 'viewer', x: 980, y: 560 },
];

/** Ключ раскладки, которую live-режим держал в браузере до `/canvases*` (адаптер досок). */
export const LEGACY_LAYOUT_PREFIX = 'shotbook_canvas_layout_';

interface LegacyLayout {
  nodes: CanvasNode[];
  connections: CanvasConnection[];
  version: number;
}

/**
 * Разовый перенос раскладки из браузера на сервер (canvas-spec.md §9). Если у
 * документа на сервере ещё не было ни одной записи (`version === 1`), а локально
 * лежат узлы — они уходят первой версией, ключ забывается, документ перечитывается.
 * Ответ 4xx (например, кадр больше лимита) тоже снимает ключ: повторять бессмысленно.
 */
export async function migrateLegacyLayout(
  canvasId: number,
  doc: CanvasDocument,
): Promise<CanvasDocument> {
  const key = `${LEGACY_LAYOUT_PREFIX}${canvasId}`;
  const layout = local.getJson<LegacyLayout>(key);
  if (!layout) return doc;
  if (doc.version !== 1 || !Array.isArray(layout.nodes) || layout.nodes.length === 0) {
    local.remove(key);
    return doc;
  }
  try {
    await canvasesApi.saveDocument(canvasId, {
      nodes: layout.nodes,
      connections: Array.isArray(layout.connections) ? layout.connections : [],
      version: 1,
    });
    local.remove(key);
    return (await canvasesApi.document(canvasId)).data;
  } catch (error) {
    if (isApiError(error) && error.status >= 400 && error.status < 500) local.remove(key);
    return doc;
  }
}

export const httpCanvasSync: CanvasSync = {
  async load(canvasId) {
    const doc = (await canvasesApi.document(canvasId)).data;
    return migrateLegacyLayout(canvasId, doc);
  },
  save(canvasId, payload) {
    return canvasesApi.saveDocument(canvasId, payload);
  },
  subscribePresence(_canvasId, onChange) {
    // TODO(Q2): реальное присутствие из канала реалтайма; в live-режиме без него курсоров нет.
    if (appConfig.apiMode !== 'mock') {
      onChange([]);
      return () => {};
    }
    let cursors = DEMO_PRESENCE.map((c) => ({ ...c }));
    onChange(cursors);
    const timer = setInterval(() => {
      cursors = cursors.map((c) => ({
        ...c,
        x: c.x + (Math.random() - 0.5) * 160,
        y: c.y + (Math.random() - 0.5) * 100,
      }));
      onChange(cursors);
    }, 1800);
    return () => clearInterval(timer);
  },
};
